import { PropsWithChildren, useState } from "react";

import { ZodProvider } from "@autoform/zod";
import { get } from "lodash";
import { LinkIcon } from "lucide-react";

import { LoadingButton } from "@/components/common/loading-button";
import { RemoteSelect } from "@/components/fields/remote-select";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { useArtistCreateForm } from "@/pages/artists/create/hooks/use-artist-create-form";
import { basicArtistSchema } from "@/pages/artists/schemas/artist.create.form";
import { SpotifyArtist } from "@/pages/artists/types/spotify-artist";

interface ArtistCreateBasicInfoStepProps {}

const schemaProvider = new ZodProvider(basicArtistSchema);

export const ArtistCreateBasicInfoStep = ({
  children,
}: PropsWithChildren<ArtistCreateBasicInfoStepProps>) => {
  const [spotifyUrl, setSpotifyUrl] = useState<string>("");
  const {
    onSpotifyArtistSelected,
    onSpotifySearch,
    onSpotifyLink,
    onBasicInfoStepSubmit,
    selectedSpotifyArtist,
    basicInfoStep,
    loading,
  } = useArtistCreateForm();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-1 w-full">
        <RemoteSelect<SpotifyArtist>
          parentClassName="w-1/2"
          onValueChanged={(value) =>
            value ? onSpotifyArtistSelected(value) : () => {}
          }
          onSearch={onSpotifySearch}
          label="Search Artist on Spotify"
          accessorFn={(option) => {
            if (!option) {
              return;
            }
            return {
              id: option.id,
              name: `${option.name}`,
              number: `${option.popularity}`,
              iconUrl: option.images ? get(option, "images[0].url") : undefined,
            };
          }}
        />
        <div className="flex flex-row space-x-2 items-end w-1/2">
          <div className="flex-col space-y-2 flex-1">
            <div className="text-sm font-medium">Spotify Link</div>
            <Input
              type="url"
              startIcon={LinkIcon}
              value={spotifyUrl}
              onChange={(e) => setSpotifyUrl(e.target.value)}
            />
          </div>
          <Button type="button" onClick={() => onSpotifyLink(spotifyUrl)}>
            Fetch
          </Button>
        </div>
      </div>
      <Separator />
      {selectedSpotifyArtist && (
        <div className="flex flex-col gap-2">
          <div>
            <strong>Genres:</strong> {selectedSpotifyArtist.genres.join(", ")}
          </div>
          <div>
            <strong>Popularity:</strong> {selectedSpotifyArtist.popularity}
          </div>
          <div>
            <strong>Followers:</strong> {selectedSpotifyArtist.followers.total}
          </div>
          {selectedSpotifyArtist.images &&
            selectedSpotifyArtist.images.length > 0 &&
            selectedSpotifyArtist.images?.[0].url && (
              <div className="flex flex-col items-start gap-1">
                <div className="text-sm font-medium">Spotify Image</div>
                <img
                  src={selectedSpotifyArtist.images?.[0].url}
                  alt="Spotify Artist"
                  className="w-32 h-32 object-cover rounded"
                />
              </div>
            )}
        </div>
      )}
      <AutoForm
        schema={schemaProvider}
        values={basicInfoStep}
        onSubmit={onBasicInfoStepSubmit}
        formProps={{}}
        {...injectCustomAutoFormComponents()}
      >
        <div className="flex justify-end">
          <LoadingButton
            disabled={loading.basicInfo === "loading"}
            isLoading={loading.basicInfo === "loading"}
          >
            Submit
          </LoadingButton>
        </div>
      </AutoForm>
    </div>
  );
};
